/**
 * Row 2 API client. Same-origin (the SPA is served by the FastAPI app), so
 * requests use relative `/api/v1` paths and `credentials: 'include'` to carry the
 * httpOnly finance session cookie. No token is ever held in the browser.
 */

const BASE = '/api/v1';

export type Exposure = 'undecided' | 'included' | 'excluded';

export interface AdminTxn {
  id: number;
  hash_id: string;
  account_id: number;
  source: string;
  signed_amount: string;
  currency: string;
  booking_date: string | null;
  value_date: string | null;
  status: string;
  provider_txn_ref: string | null;
  narrative: string | null;
  bank_txn_code: string | null;
  end_to_end_id: string | null;
  reversal_indicator: boolean | null;
  exposure: Exposure;
  exposure_decided_at: string | null;
  exposure_decided_by: string | null;
  account_provider_id: string;
  account_bic: string | null;
}

interface Page<T> {
  items: T[];
  next_cursor: string | null;
}

export class Unauthorized extends Error {}

/** 423 from the API: the cutover-window exposure freeze (stage-2 rev 5). */
export class ExposureFrozen extends Error {}

async function req<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    ...init,
  });
  if (res.status === 401) {
    throw new Unauthorized('unauthorized');
  }
  if (res.status === 423) {
    // Stage-2 rev 5: exposure decisions are frozen during the cutover window.
    throw new ExposureFrozen('exposure decisions frozen');
  }
  if (!res.ok) {
    throw new Error(`request failed: ${res.status}`);
  }
  return (await res.json()) as T;
}

/** POST /login — returns true on success, false on a rejected passphrase. */
export async function login(passphrase: string): Promise<boolean> {
  const res = await fetch(`${BASE}/login`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ passphrase }),
  });
  if (res.ok) return true;
  if (res.status === 401) return false;
  throw new Error(`login failed: ${res.status}`);
}

export interface AccountRef {
  account_id: number;
  provider_id: string;
  bic: string | null;
  source: string;
}

export interface Balance {
  account_id: number;
  currency: string;
  balance_type: string;
  signed_amount: string;
  balance_date: string | null;
  source: string;
  updated_time: string;
}

export interface TxnFilter {
  exposure: Exposure | 'all';
  q?: string; // search over transaction ID, end-to-end ref and description
  source?: string; // bank (source discriminator)
  account?: number; // internal account_id
  from?: string; // ISO date (booking_date >=)
  to?: string; // ISO date (booking_date <=)
  cursor?: string; // opaque keyset cursor for the next page
  limit?: number;
}

export interface TxnPage {
  items: AdminTxn[];
  nextCursor: string | null;
}

/** GET /admin/transactions — the finance review queue for one exposure state,
 * with optional date range + keyset pagination. */
export async function listTransactions(filter: TxnFilter): Promise<TxnPage> {
  const q = new URLSearchParams({ exposure: filter.exposure, limit: String(filter.limit ?? 50) });
  if (filter.source) q.set('source', filter.source);
  if (filter.account !== undefined) q.set('account', String(filter.account));
  if (filter.from) q.set('from', filter.from);
  if (filter.to) q.set('to', filter.to);
  if (filter.q) q.set('q', filter.q);
  if (filter.cursor) q.set('cursor', filter.cursor);
  const page = await req<Page<AdminTxn>>(`/admin/transactions?${q.toString()}`);
  return { items: page.items, nextCursor: page.next_cursor };
}

/** GET /admin/accounts — the accounts for the filter dropdown + banks view. */
export async function listAccounts(): Promise<AccountRef[]> {
  return req<AccountRef[]>(`/admin/accounts`);
}

/** GET /admin/balances — latest balances for the finance banks/balance overview. */
export async function listBalances(): Promise<Balance[]> {
  return req<Balance[]>(`/admin/balances`);
}

/** POST /logout — clear the finance session (server expires the httpOnly cookie).
 * Throws on a non-2xx so the caller doesn't show "signed out" while the cookie lives. */
export async function logout(): Promise<void> {
  const res = await fetch(`${BASE}/logout`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
  });
  if (!res.ok) {
    throw new Error(`logout failed: ${res.status}`);
  }
}

/** POST /transactions/{id}/exposure — set the include/exclude decision. */
export async function setExposure(id: number, decision: 'included' | 'excluded'): Promise<void> {
  await req(`/transactions/${id}/exposure`, {
    method: 'POST',
    body: JSON.stringify({ decision }),
  });
}
